import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Modal } from "react-bootstrap"
import * as XLSX from "xlsx"
import { PGET } from "../../../helpers/api"
import { MONTHS, DEALER_TYPES, filterByType, fmt, percentOf, colorOf, usePlanFactData, PfStyles } from "./common"

function Fakt() {
	const { t } = useTranslation()

	const now = new Date()
	const [month, setMonth] = useState(now.getMonth() + 1)
	const [year, setYear] = useState(now.getFullYear())
	const [dealerType, setDealerType] = useState("all")

	const { rows, loading } = usePlanFactData(month, year)

	const [selectedDealer, setSelectedDealer] = useState(null)
	const [posRows, setPosRows] = useState([])
	const [posLoading, setPosLoading] = useState(false)

	const years = useMemo(() => {
		const y = new Date().getFullYear()
		return [y - 2, y - 1, y, y + 1]
	}, [])

	const shownRows = useMemo(() => filterByType(rows, dealerType), [rows, dealerType])

	const totals = useMemo(() => {
		const plan = shownRows.reduce((s, r) => s + (Number(r.plan) || 0), 0)
		const fact = shownRows.reduce((s, r) => s + (Number(r.fact) || 0), 0)
		return { plan, fact, percent: percentOf(plan, fact) }
	}, [shownRows])

	async function openDealer(dealer) {
		setSelectedDealer(dealer)
		setPosRows([])
		setPosLoading(true)
		try {
			const res = await PGET(`/services/admin/api/plan-fact/${dealer.id}/poses`, { year, month }, { loader: false })
			const data = Array.isArray(res?.data) ? res.data : res?.data?.data
			setPosRows(data || [])
		} catch (e) {
			setPosRows([])
		} finally {
			setPosLoading(false)
		}
	}

	function closeDealer() {
		setSelectedDealer(null)
		setPosRows([])
	}

	function exportExcel() {
		const excelData = shownRows.map((r, i) => {
			const p = percentOf(r.plan, r.fact)
			return {
				"№": i + 1,
				Diler: r.name,
				"Reja (so'm)": r.plan,
				"Fakt (so'm)": r.fact,
				Bajarilishi: p === null ? "—" : `${p}%`,
			}
		})
		const ws = XLSX.utils.json_to_sheet(excelData)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, "Fakt")
		XLSX.writeFile(wb, `fakt_${year}-${String(month).padStart(2, "0")}.xlsx`)
	}

	return (
		<div className="plan-fact">
			<div className="pf-header">
				<div>
					<h4 className="pf-title">{t("Fakt")}</h4>
					<div className="pf-subtitle">{t("Dilerlar bo'yicha haqiqiy savdo (nechta qilgani)")}</div>
				</div>
				<div className="pf-controls">
					<select className="pf-select" value={dealerType} onChange={(e) => setDealerType(e.target.value)}>
						{DEALER_TYPES.map((d) => (
							<option key={d.value} value={d.value}>{t(d.i18n)}</option>
						))}
					</select>
					<select className="pf-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
						{MONTHS.map((m, i) => (
							<option key={i} value={i + 1}>{t(m)}</option>
						))}
					</select>
					<select className="pf-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
						{years.map((y) => (
							<option key={y} value={y}>{y}</option>
						))}
					</select>
					<button className="pf-btn pf-btn-outline" onClick={exportExcel} disabled={loading}>
						Excel
					</button>
				</div>
			</div>

			<div className="pf-card">
				<div className="pf-row pf-row-head">
					<div className="pf-col-name">{t("Diler")}</div>
					<div className="pf-col-num">{t("Plan (so'm)")}</div>
					<div className="pf-col-num">{t("Fakt (so'm)")}</div>
					<div className="pf-col-bar">{t("Bajarilishi")}</div>
				</div>

				{loading ? (
					<div className="pf-empty">{t("Yuklanmoqda...")}</div>
				) : shownRows.length === 0 ? (
					<div className="pf-empty">{t("LIST_IS_EMPTY")}</div>
				) : (
					shownRows.map((r, i) => {
						const p = percentOf(r.plan, r.fact)
						const c = colorOf(p)
						const clickable = (Number(r.fact) || 0) > 0
						return (
							<div
								className={"pf-row" + (clickable ? " pf-row-clickable" : "")}
								key={r.id}
								onClick={clickable ? () => openDealer(r) : undefined}
								title={clickable ? t("POS ro'yxatini ko'rish") : ""}
							>
								<div className="pf-col-name">
									<span className="pf-index">{i + 1}.</span> {r.name}
								</div>
								<div className="pf-col-num pf-plan">{fmt(r.plan)}</div>
								<div className="pf-col-num pf-fact">{fmt(r.fact)}</div>
								<div className="pf-col-bar">
									<span className="pf-chip" style={{ background: c.chip, color: c.text }}>
										{p === null ? "—" : `${p}%`}
									</span>
									<div className="pf-progress">
										<div
											className="pf-progress-fill"
											style={{ width: `${p === null ? 0 : Math.min(p, 100)}%`, background: c.bar }}
										/>
									</div>
								</div>
							</div>
						)
					})
				)}

				{shownRows.length > 0 && (
					<div className="pf-row pf-row-total">
						<div className="pf-col-name">{t("Jami")}:</div>
						<div className="pf-col-num pf-plan">{fmt(totals.plan)}</div>
						<div className="pf-col-num pf-fact">{fmt(totals.fact)}</div>
						<div className="pf-col-bar">
							<span
								className="pf-chip"
								style={{ background: colorOf(totals.percent).chip, color: colorOf(totals.percent).text }}
							>
								{totals.percent === null ? "—" : `${totals.percent}%`}
							</span>
							<div className="pf-progress">
								<div
									className="pf-progress-fill"
									style={{
										width: `${totals.percent === null ? 0 : Math.min(totals.percent, 100)}%`,
										background: colorOf(totals.percent).bar,
									}}
								/>
							</div>
						</div>
					</div>
				)}
			</div>

			<div className="pf-note">
				{t("«Fakt» real tizimda savdo/to'lov jadvalidan avtomatik hisoblanadi. Reja «Plan» bo'limida o'rnatiladi.")}
			</div>

			<Modal show={!!selectedDealer} onHide={closeDealer} centered size="lg" backdrop="static">
				<Modal.Header closeButton>
					<Modal.Title>
						{selectedDealer ? `${selectedDealer.name} — ${t("POS ro'yxati")}` : ""}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{posLoading ? (
						<div className="pf-empty">{t("Yuklanmoqda...")}</div>
					) : posRows.length === 0 ? (
						<div className="pf-empty">{t("LIST_IS_EMPTY")}</div>
					) : (
						<div className="table-responsive">
							<table className="table mb-0">
								<thead>
									<tr>
										<th>№</th>
										<th>{t("POS")}</th>
										<th>{t("Egasi")}</th>
										<th>{t("Telefon")}</th>
										<th>{t("Yaratilgan sana")}</th>
										<th className="text-end">{t("Summa (so'm)")}</th>
									</tr>
								</thead>
								<tbody>
									{posRows.map((p, i) => (
										<tr key={p.posId}>
											<td>{i + 1}</td>
											<td>{p.name}</td>
											<td>{p.login}</td>
											<td>{p.phone || "—"}</td>
											<td>{p.createdDate || "—"}</td>
											<td className="text-end">{fmt(p.amount)}</td>
										</tr>
									))}
									<tr className="pf-modal-total">
										<td colSpan={5}>{t("Jami")}</td>
										<td className="text-end">
											{fmt(posRows.reduce((s, p) => s + (Number(p.amount) || 0), 0))}
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					)}
				</Modal.Body>
			</Modal>

			<PfStyles />
			<style>{`
				.pf-row-clickable { cursor: pointer; }
				.pf-row-clickable:hover { background: #f9fafb; }
				.pf-modal-total td { font-weight: 700; background: #f9fafb; }
			`}</style>
		</div>
	)
}

export default Fakt
